import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { Card, Spinner, EmptyState, Badge } from '@/components/ui';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Operations feature toggles — turn platform features on/off without a deploy.
 *
 *  • Split fare — divide a trip's fare across riders. KEPT OFF by default: the
 *    split service isn't prepaid-integrated yet (it doesn't debit each rider's
 *    km-wallet or check credits up front), so don't enable until that rework.
 *  • Masked calling — Vonage number-masking so rider ↔ driver can call without
 *    sharing personal numbers (comms-svc).
 *  • Wait-time fees — a per-minute charge added when a rider keeps the driver
 *    waiting past a grace period at pickup (trip-svc). Set the price + grace,
 *    then enable.
 *
 *  admin-svc owns the singleton `feature_settings` row; the relevant services
 *  read it every request so a change here takes effect immediately. Gated by
 *  cms.manage to edit; degrades read-only if the table isn't applied yet. */
export function FeatureSettings() {
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'feature-settings'],
    queryFn: ({ signal }) => adminApi.featureSettings(signal),
  });

  const available = data?.available !== false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Operations"
        subtitle="Turn platform features on or off — split fare, masked calling, wait-time fees, surge pricing."
        right={<Badge tone={available ? 'ok' : 'neutral'}>{available ? 'live' : 'not configured'}</Badge>}
      />

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading settings…" />
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState title="Couldn't load settings" description={(error as Error)?.message} />
        </Card>
      ) : !available ? (
        <Card>
          <EmptyState
            title="Not configured yet"
            description="The feature_settings table isn’t in this database yet. It appears once the migration is applied."
          />
        </Card>
      ) : data ? (
        <Form
          split={data.split_fare_enabled}
          masked={data.masked_calling_enabled}
          waitEnabled={data.wait_time_enabled}
          waitPrice={data.wait_time_price_per_min}
          waitGrace={data.wait_time_grace_min}
          pickupSurge={data.pickup_surge_enabled}
          pickupFlat={data.pickup_surge_flat_day_rate}
          shortRideFlat={data.short_ride_surge_flat_day_rate}
          canManage={canManage}
        />
      ) : (
        <Card>
          <EmptyState title="No settings" />
        </Card>
      )}
    </AdminPage>
  );
}

function Form({
  split,
  masked,
  waitEnabled,
  waitPrice,
  waitGrace,
  pickupSurge,
  pickupFlat,
  shortRideFlat,
  canManage,
}: {
  split: boolean;
  masked: boolean;
  waitEnabled: boolean;
  waitPrice: number;
  waitGrace: number;
  pickupSurge: boolean;
  pickupFlat: boolean;
  shortRideFlat: boolean;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [s, setS] = useState(split);
  const [m, setM] = useState(masked);
  const [w, setW] = useState(waitEnabled);
  const [price, setPrice] = useState(String(waitPrice));
  const [grace, setGrace] = useState(String(waitGrace));
  const [pu, setPu] = useState(pickupSurge);
  const [puFlat, setPuFlat] = useState(pickupFlat);
  const [srFlat, setSrFlat] = useState(shortRideFlat);
  const [clientError, setClientError] = useState<string | null>(null);

  useEffect(() => {
    setS(split);
    setM(masked);
    setW(waitEnabled);
    setPrice(String(waitPrice));
    setGrace(String(waitGrace));
    setPu(pickupSurge);
    setPuFlat(pickupFlat);
    setSrFlat(shortRideFlat);
  }, [split, masked, waitEnabled, waitPrice, waitGrace, pickupSurge, pickupFlat, shortRideFlat]);

  const mutation = useMutation({
    mutationFn: (input: {
      split_fare_enabled: boolean;
      masked_calling_enabled: boolean;
      wait_time_enabled: boolean;
      wait_time_price_per_min: number;
      wait_time_grace_min: number;
      pickup_surge_enabled: boolean;
      pickup_surge_flat_day_rate: boolean;
      short_ride_surge_flat_day_rate: boolean;
    }) => adminApi.updateFeatureSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'feature-settings'] }),
  });

  const dirty =
    s !== split ||
    m !== masked ||
    w !== waitEnabled ||
    String(waitPrice) !== price.trim() ||
    String(waitGrace) !== grace.trim() ||
    pu !== pickupSurge ||
    puFlat !== pickupFlat ||
    srFlat !== shortRideFlat;

  const onSave = () => {
    setClientError(null);
    const p = Number(price.trim());
    const g = Number(grace.trim());
    if (!Number.isFinite(p) || p < 0 || p > 100) {
      setClientError('Wait-time price must be between $0 and $100 per minute.');
      return;
    }
    if (!Number.isInteger(g) || g < 0 || g > 30) {
      setClientError('Grace period must be a whole number of minutes between 0 and 30.');
      return;
    }
    mutation.mutate({
      split_fare_enabled: s,
      masked_calling_enabled: m,
      wait_time_enabled: w,
      wait_time_price_per_min: p,
      wait_time_grace_min: g,
      pickup_surge_enabled: pu,
      pickup_surge_flat_day_rate: puFlat,
      short_ride_surge_flat_day_rate: srFlat,
    });
  };

  return (
    <div className="space-y-4">
      {/* Split fare */}
      <Card>
        <Toggle
          label="Split fare"
          desc="Let a rider divide a trip’s fare across others."
          checked={s}
          disabled={!canManage}
          onChange={setS}
        />
        {s ? (
          <p className="mt-2 text-xs text-warning">
            ⚠ Split fare isn’t prepaid-integrated yet — it won’t debit each rider’s km-wallet or check credits up
            front. Keep it off until that rework, or it may divide a fare without charging correctly.
          </p>
        ) : null}
      </Card>

      {/* Masked calling */}
      <Card>
        <Toggle
          label="Masked calling"
          desc="Rider ↔ driver can call/text via a proxy number, keeping personal numbers private."
          checked={m}
          disabled={!canManage}
          onChange={setM}
        />
      </Card>

      {/* Wait-time fees */}
      <Card>
        <Toggle
          label="Wait-time fees"
          desc="Charge a per-minute fee when a rider keeps the driver waiting past the grace period at pickup."
          checked={w}
          disabled={!canManage}
          onChange={setW}
        />
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-[11px] text-text-muted">Price per minute ($)</label>
            <div className="mt-1 flex items-center">
              <span className="mr-1.5 text-sm text-text-muted">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                max="100"
                step="0.05"
                value={price}
                disabled={!canManage}
                onChange={(e) => setPrice(e.target.value)}
                className="input w-32"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-text-muted">Grace period (minutes)</label>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              max="30"
              step="1"
              value={grace}
              disabled={!canManage}
              onChange={(e) => setGrace(e.target.value)}
              className="input mt-1 w-32"
            />
          </div>
        </div>
      </Card>

      {/* Surge pricing */}
      <Card>
        <div className="mb-3 text-[13px] font-semibold text-slate-100">Surge pricing</div>

        <Toggle
          label="Pickup surge"
          desc="Charge the rider for the matched driver's distance to the pickup (Premier & Logistics only). Kept by Telride, shown on the bill as a generic “Surge”. OFF = don’t charge it."
          checked={pu}
          disabled={!canManage}
          onChange={setPu}
        />

        <div className="mt-4 border-t border-white/5 pt-4">
          <Toggle
            label="Pickup surge — flat day rate"
            desc="ON = always price the pickup leg at the flat day rate ($4.25/km). OFF = time-banded (day $4.25 / evening $6.25 / night $10.85)."
            checked={puFlat}
            disabled={!canManage || !pu}
            onChange={setPuFlat}
          />
          {!pu ? <p className="mt-1 text-[11px] text-text-muted">Enable pickup surge to set its rate.</p> : null}
        </div>

        <div className="mt-4 border-t border-white/5 pt-4">
          <Toggle
            label="Short-ride surge — flat day rate"
            desc="The +1km charge on a ride of 2km or less (always on, paid 100% to the driver). ON = flat day rate ($4.25). OFF = time-banded."
            checked={srFlat}
            disabled={!canManage}
            onChange={setSrFlat}
          />
        </div>
      </Card>

      {canManage ? (
        <div className="flex items-center gap-3">
          <button type="button" disabled={mutation.isPending || !dirty} onClick={onSave} className="btn-primary">
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          {mutation.isSuccess && !dirty ? <span className="text-xs text-success">Saved.</span> : null}
        </div>
      ) : (
        <p className="text-xs text-text-muted">You don’t have permission to edit these settings.</p>
      )}

      {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
      {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
    </div>
  );
}

function Toggle({
  label,
  desc,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4">
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-slate-100">{label}</span>
        <span className="mt-0.5 block text-xs text-text-secondary">{desc}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 shrink-0 accent-brand-500"
      />
    </label>
  );
}
